import * as Sentry from '@sentry/vue';
import { denyUrls, ignoreErrors, isErrorInstance } from '@utils/error-tracking';
import { isNavigationFailure, NavigationFailure, NavigationFailureType } from 'vue-router';
import { isSuppressedStatusCode, processResponseData } from './error-tracking';
import { App } from 'vue';
import { AxiosError } from 'axios';
import { createSentryPiniaPlugin } from '@sentry/vue';
import { getSessionReplayLink } from './datadog';
import { sanitizeNumbers } from '@filters/shared-filters';
import { useAppStore } from '@stores/app';
import { userAgentIsBot } from './bot-detection';
import type { ErrorEvent, EventHint } from '@sentry/vue';
import type { StateTree } from 'pinia';

export function initSentry(app: App<Element>) {
	Sentry.init({
		app,
		ignoreErrors,
		denyUrls,
		dsn: `${import.meta.env.VITE_SENTRY_DSN}`,
		environment: `${import.meta.env.VITE_SENTRY_ENVIRONMENT}`,
		enabled: import.meta.env.VITE_SENTRY_ENABLED === 'true',
		attachProps: false,
		sendDefaultPii: true, // includes IP address
		beforeSend: (event, hint) => sentryBeforeSendHandler(event, hint),
		integrations: [
			Sentry.vueIntegration({
				tracingOptions: {
					trackComponents: true
				}
			}),
			Sentry.extraErrorDataIntegration(),
			Sentry.launchDarklyIntegration()
		],
		release: import.meta.env.VITE_SENTRY_RELEASE_ID
	});
	Sentry.setTag('Application', 'Vue');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filterOriginalExceptionTokens(exception: any) {
	let updatedException = exception;
	if (typeof exception === 'string') {
		const bearerRegEx = /"Bearer([\s\S]+?)"/gi;
		updatedException = exception.replace(bearerRegEx, '"Bearer XXX"');
	}

	if (exception.config?.headers?.['authorization']) {
		updatedException.config.headers['authorization'] = 'XXX';
	}

	return updatedException;
}

function generateAxiosErrorFingerPrint(exception: AxiosError): string[] {
	const newFingerprint = ['axios'];
	const responseStatus = exception.response?.status;
	const sanitizedUrl =
		responseStatus !== 403 && exception.message.toLowerCase() !== 'network error'
			? sanitizeNumbers(exception.config.url)
			: undefined;

	if (sanitizedUrl) {
		// These urls frequently end in a random string, but we want to group them together under the main endpoint.
		if (sanitizedUrl?.includes('/promo/referrer-by-key')) {
			newFingerprint.push('/promo/referrer-by-key');
		} else {
			newFingerprint.push(sanitizedUrl);
		}
	}

	if (responseStatus) {
		newFingerprint.push(`${responseStatus}`);
	}

	if (exception.message) {
		const sanitizedMessage = sanitizeNumbers(exception.response?.data?.errorDescription);
		newFingerprint.push(sanitizedMessage ?? exception.message);
	}

	return newFingerprint;
}

function setAxiosContext(event: ErrorEvent, exception: AxiosError) {
	const contexts = { ...event.contexts };
	contexts.Axios = {
		url: exception?.config?.url ? sanitizeNumbers(exception.response?.config.url) : undefined,
		data: processResponseData(exception.response?.data),
		status: exception.response?.status
	};

	return contexts;
}

function handleAxiosResponseError(event: ErrorEvent, exception: AxiosError) {
	const isSuppressedStatus = isSuppressedStatusCode(exception);
	if (isSuppressedStatus) {
		return null;
	}

	event.contexts = setAxiosContext(event, exception);

	event.fingerprint = generateAxiosErrorFingerPrint(exception);

	// Provides descriptive Sentry message instead of "Request failed with status code XXX"
	if (event.exception?.values?.[0]) {
		const responseData = exception.response?.data;
		const initialExceptionValue = event.exception.values[0].value;
		const requestEndpoint = exception?.config?.url;
		let errorMessage = `${initialExceptionValue} on endpoint ${requestEndpoint}`;

		if (responseData?.errorDescription) {
			errorMessage = responseData.errorDescription;
		} else if (responseData?.detail) {
			errorMessage = responseData.detail;
		}

		event.exception.values[0].value = errorMessage;
	}

	return event;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isRouterNavigationFailure(exception: any): exception is NavigationFailure {
	return (
		Object.values(NavigationFailureType).includes(exception.type) &&
		exception.hasOwnProperty('to') &&
		isNavigationFailure(exception)
	);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function handleVueRouterError(event: ErrorEvent, exception: any) {
	if (isNavigationFailure(exception, NavigationFailureType.aborted)) {
		return null;
	}

	const navError = exception as NavigationFailure;
	const errorTypeMap = {
		1: 'MATCHER_NOT_FOUND',
		2: 'NAVIGATION_GUARD_REDIRECT',
		4: 'NAVIGATION_ABORTED',
		8: 'NAVIGATION_CANCELLED',
		16: 'NAVIGATION_DUPLICATED'
	};
	const errorType = errorTypeMap[navError.type];
	const toPath = sanitizeNumbers(navError.to?.path) ?? 'unknown';
	const fromPath = sanitizeNumbers(navError.from?.path) ?? 'unknown';

	event.fingerprint = ['vue-router', errorType, toPath];
	event.contexts = { ...event.contexts, VueRouter: { fromPath, toPath, errorType, params: navError.to?.params } };
	if (event.exception?.values?.[0]) {
		event.exception.values[0].value = `Vue Router: ${errorTypeMap[navError.type]}: ${fromPath} -> ${toPath}`;
	}

	return event;
}

function addEventContext(event: ErrorEvent) {
	const contexts = { ...event.contexts };
	const sessionReplayLink = getSessionReplayLink();

	if (sessionReplayLink) {
		contexts.Datadog = { sessionReplayLink };
	}

	return contexts;
}

function addEventTags(event: ErrorEvent) {
	const appStore = useAppStore();
	const tags = { ...event.tags };
	tags.isCorniceSubstituteUser = appStore.isCorniceSubstituteUser;
	tags.isMobileWebview = appStore.isMobileWebview;
	tags.isAdvisor = appStore.isAdvisor;
	tags.hasReplay = !!getSessionReplayLink();
	tags.wafIsLoaded = appStore.wafIsLoaded;
	tags.isBot = userAgentIsBot();

	return tags;
}

function filterUrlQueryParams(url: string): string {
	const requestUrl = new URL(url);
	const queryParams = requestUrl?.searchParams;
	const paramsToFilter = ['key', 'passwordResetKey'];
	paramsToFilter.forEach((param) => {
		const keyParam = queryParams.get(param);
		if (keyParam) {
			queryParams.set(param, 'XXX');
			requestUrl.search = queryParams.toString();
		}
	});

	return requestUrl.toString();
}

export function sentryBeforeSendHandler(event: ErrorEvent, hint: EventHint) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const exception = hint?.originalException as any;
	const exceptionIsObject = exception === Object(exception);
	const appStore = useAppStore();

	if (appStore.isThirdPartyLogin) {
		return null;
	}

	if (event.request?.url) {
		event.request.url = filterUrlQueryParams(event.request.url);
	}

	if (event.request?.headers?.['authorization']) {
		event.request.headers['authorization'] = 'XXX';
	}

	event.contexts = addEventContext(event);
	event.tags = addEventTags(event);

	if (exception) {
		hint.originalException = filterOriginalExceptionTokens(exception);
	}

	if (exceptionIsObject && exception.isAxiosError) {
		return handleAxiosResponseError(event, exception as AxiosError);
	}

	if (exceptionIsObject && isRouterNavigationFailure(exception)) {
		return handleVueRouterError(event, exception);
	}

	if (isErrorInstance(exception) && exception.message === 'WAF retry failed (may be invalid token)') {
		event.fingerprint = ['waf-challenge-failure'];
	}

	return event;
}

export function sanitizeState(state: StateTree): StateTree {
	const SENSITIVE_PATTERNS = [
		/name/i,
		/email/i,
		/token/i,
		/password/i,
		/bank/i,
		/routing/i,
		/key/i,
		/paymentMethods/i,
		/address/i,
		/phone/i,
		/ssn/i
	];

	const sanitized: StateTree = Array.isArray(state) ? [] : ({} as StateTree);
	for (const [key, value] of Object.entries(state)) {
		if (SENSITIVE_PATTERNS.some((p) => p.test(key))) {
			sanitized[key] = '<REDACTED>';
		} else if (typeof value === 'object' && value !== null) {
			sanitized[key] = sanitizeState(value as StateTree);
		} else {
			sanitized[key] = value;
		}
	}
	return sanitized;
}

export function createSanitizedSentryPiniaPlugin() {
	return createSentryPiniaPlugin({
		stateTransformer: sanitizeState
	});
}
